import { useMutation, useQuery } from "@tanstack/react-query";

import { CreateBugReportRequestI } from "@/api/routes/bug-reports";
import { useApiClient } from "@/context/api-client";

export const BugReportsQueryKeys = {
  status: () => ["bug-reports", "status"] as const,
};

/**
 * Whether bug reports are on (GLE-8483). It fails closed: an error, or a
 * backend without the route, reads as off. No retries. A failure is a cached
 * `false`, so a later failed refresh hides the button, and a remount does not
 * ask again before the five minutes pass.
 */
export const useFetchBugReportsSwitch = () => {
  const api = useApiClient();

  return useQuery({
    queryKey: BugReportsQueryKeys.status(),
    queryFn: async () => {
      try {
        const { data } = await api.bugReports.getBugReportStatus();

        return data?.enabled === true;
      } catch {
        return false;
      }
    },
    retry: false,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
};

export const useCreateBugReport = () => {
  const api = useApiClient();

  return useMutation({
    mutationFn: async (body: CreateBugReportRequestI) => {
      const { data } = await api.bugReports.createBugReport(body);

      return data;
    },
  });
};
